import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import toast from 'react-hot-toast';
import { postRequest } from '../../../lib/api';
import { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../../../authContext/context';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';
import { useSearchParams } from 'react-router-dom';
import {
  UserRole,
  UserStatus,
  ROLE_LABELS,
  getRoleOptionsForUser,
  getHierarchyLevel
} from '../../../constants/auth.constants';

function generatePassword(length = 12) {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*?';
  const all = upper + lower + digits + special;

  let password = '';
  password += upper[Math.floor(Math.random() * upper.length)];
  password += lower[Math.floor(Math.random() * lower.length)];
  password += digits[Math.floor(Math.random() * digits.length)];
  password += special[Math.floor(Math.random() * special.length)];

  for (let i = 4; i < length; i++) {
    password += all[Math.floor(Math.random() * all.length)];
  }

  return password
    .split('')
    .sort(() => 0.5 - Math.random())
    .join('');
}

export default function CreateUser() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState(generatePassword());
  const [selectedRole, setSelectedRole] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);

  const [searchParams] = useSearchParams();
  const parentId = searchParams.get('parent_id') || searchParams.get('partner_id');

  useEffect(() => {
    const userRole = authCtx.role;
    const roleOptions = getRoleOptionsForUser(userRole);
    setAvailableRoles(roleOptions);

    if (parentId && userRole === UserRole.SUPERADMIN) {
      setSelectedRole(UserRole.PARTNER_USER);
    } else if (roleOptions.length > 0) {
      setSelectedRole(roleOptions[0].value);
    }
  }, [authCtx.role, parentId]);

  const regeneratePassword = () => {
    const newPass = generatePassword();
    setTempPassword(newPass);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (!selectedRole) {
        toast.error('Please select a user role');
        setLoading(false);
        return;
      }

      const payload = {
        email: data.email,
        password: tempPassword,
        forename: data.firstName,
        surname: data.lastName,
        username: data.username,
        phone_number: data.phone_number,
        role: selectedRole,
        hierarchyLevel: getHierarchyLevel(selectedRole),
        status: UserStatus.PENDING,
        createdBy: authCtx.user?.userId,
        parentId: parentId || null,
        partner_id: parentId || null  // Also send partner_id when creating from partner view
      };

      if (selectedRole === UserRole.PARTNER_USER && !parentId) {
        toast.error('Partner ID is required to create a Partner User');
        setLoading(false);
        return;
      }

      if (selectedRole === UserRole.SOLICITOR) {
        payload.parentId = authCtx.user?.userId;
        payload.partner_id = null;  // Solicitors don't have partner_id
      }

      console.log('🚀 Sending payload to /register:', { ...payload, password: '***' });

      const response = await postRequest('private/register', payload, authCtx.token);

      if (response?.status === 201 || response?.success) {
        reset();
        setTempPassword(generatePassword());
        navigate(`/user/view/${response?.data?._id}`);
      }
    } catch (err) {
      console.error('User creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (availableRoles.length === 0) {
    return (
      <>
        <BreadcrumbsWrapper
          items={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Users', path: '/users' },
            { label: 'Add User', path: '' },
          ]}
        />
        <br></br>
        <div className="page-header">
          <h1 className="mt-0">Add New User</h1>
        </div>
        <div className="alert alert-warning">
          You do not have permission to create users.
        </div>
      </>
    );
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Users', path: '/users' },
          { label: 'Add User', path: '' },
        ]}
      />
      <br></br>
      <div className="page-header">
        <h1 className="mt-0">Add New User</h1>
      </div>

      <div className="page-top-formview mb-3">
        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Role Selection */}
          {/* <Row className="mb-3">
            <Form.Group as={Col} controlId="role">
              <Form.Label>User Role</Form.Label>
              <Form.Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                disabled={loading}
                isInvalid={!selectedRole}
                required
              >
                <option value="">Select Role</option>
                {availableRoles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Please select a role
              </Form.Control.Feedback>
            </Form.Group>
          </Row> */}

          {/* Personal Information */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="firstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="First Name"
                {...register('firstName', { required: 'First name is required' })}
                isInvalid={!!errors.firstName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="lastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Last Name"
                {...register('lastName', { required: 'Last name is required' })}
                isInvalid={!!errors.lastName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* Contact Information */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email address',
                  },
                })}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="phone_number">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Phone Number"
                {...register('phone_number')}
              />
            </Form.Group>
          </Row>

          {/* Account Information */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="username">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Username"
                {...register('username', {
                  required: 'Username is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum 3 characters required',
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_-]+$/,
                    message: 'Only letters, numbers, underscore and hyphen allowed'
                  }
                })}
                isInvalid={!!errors.username}
              />
              <Form.Control.Feedback type="invalid">
                {errors.username?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* Temporary Password */}
          <div className="alert alert-info mb-3">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <strong>Temporary Password:</strong>{' '}
                <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                  {tempPassword}
                </code>
                <br />
                <small>The user will be prompted to change this password on first login.</small>
              </div>
              <Button
                size="small"
                onClick={regeneratePassword}
                disabled={loading}
                variant="text"
                style={{ minWidth: 'auto', padding: '4px 8px' }}
              >
                🔄
              </Button>
            </div>
          </div>

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="inherit" type="button" onClick={() => navigate('/users')}>
              Cancel
            </Button>
            <Button variant="contained" className='red-bg red-button text-capitalize' type="submit" disabled={loading || !selectedRole}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
            </Button>
          </Stack>
        </Form>
      </div>
    </>
  );
}
